import { teamsCallUrl } from '@lib/care-model';
import { callCheck, type CallCheckResp, type CallLine } from '@lib/care-api';
import { CALL_CHECK_FAILED_SENTENCE } from '@lib/receive-only';

/**
 * W9 (00 Main's RULED 01.59.17 AM, 11 Oct 2026, from Care's own finding): a guarded Call button never opens Teams
 * by itself. It asks the server first (POST /api/care/call-check) and opens Teams only on a plain yes; a no, an
 * error or no answer leaves the call unplaced and gives the ONE sentence to show (fail closed).
 *
 * The Teams tab is opened BLANK in the press itself, before the question, because a tab opened only after an
 * answer has come back is one a pop-up blocker may stop; on a yes it is sent to Teams, on anything else it is
 * closed again. With no tab (a blocker stopped even that), a yes opens Teams the plain way.
 *
 * BOUND TO THE CASE (T-1010-99-CALLBIND, the office's DECIDED of 1:46 PM 11 Oct 2026): the press sends the CASE
 * and which of its numbers (`target`), never the number. The server reads the number from the case and a yes
 * carries the number it checked; Teams is opened for THAT number, so the number dialed is always the number
 * checked. A yes that carries no dialable number is a no. The number on screen is used only to decide whether
 * there is anything to ask.
 *
 * EVERY MEMBER CALL (the office, 17 Oct 2026, interim line 5): every place in this console that starts a member call
 * goes through placeCall: the out-calling worklist's Call (QueueTable), the case drawer's Call beside the from-phone
 * and beside each of the member's roster numbers (PhoneActions, given the case), and the call panel's "Start
 * call in Teams" (CallFeeder). There is no plain Teams link left.
 */
export type CallOutcome = { placed: true } | { placed: false; said: string };

/** The case a Call button stands on, and which of the case's numbers it shows. */
export interface CallTarget { eventId: string; line: CallLine }

const digitsOf = (v: string | undefined | null): string => (v ?? '').replace(/\D/g, '').slice(-10);

/**
 * Which of the matched member's two roster numbers a number shown in the drawer's member section is: 'phone1' when
 * it is the member's first number, else 'phone2'. The server reads that line from the case, so a number that is the
 * member's second number is checked (and dialed) as the case's second number.
 */
export function rosterLineOf(m: { phone1?: string | null; phone2?: string | null } | null | undefined, phone: string): CallLine {
  const p = digitsOf(phone);
  return p !== '' && p === digitsOf(m?.phone1) ? 'phone1' : 'phone2';
}

/**
 * The call panel's line (components/CallFeeder.tsx): it shows the entry's from-phone, else the matched member's first
 * roster number, so its press names 'from' or 'phone1' to match the number it shows.
 */
export function feederLineOf(ev: { fromPhone?: string | null }): CallLine {
  return digitsOf(ev.fromPhone) !== '' ? 'from' : 'phone1';
}

export async function placeCall(phone: string, target: CallTarget): Promise<CallOutcome> {
  if (!teamsCallUrl(phone)) return { placed: false, said: 'No phone number to dial.' };
  let tab: Window | null = null;
  try {
    tab = window.open('', '_blank') ?? null;
    if (tab) tab.opener = null;
  } catch {
    tab = null;
  }
  let url: string | null = null;
  let said = '';
  try {
    const r = await callCheck(target.eventId, target.line);
    const d = (r.data ?? {}) as CallCheckResp;
    if (r.ok && d.callable === true) url = teamsCallUrl(typeof d.phone === 'string' ? d.phone : '');
    said = typeof d.said === 'string' ? d.said : '';
  } catch {
    url = null;
  }
  if (url) {
    if (tab) tab.location.replace(url);
    else window.open(url, '_blank', 'noopener');
    return { placed: true };
  }
  try {
    tab?.close();
  } catch {
    /* the tab is already gone */
  }
  return { placed: false, said: said || CALL_CHECK_FAILED_SENTENCE };
}
